import { useCallback, useEffect, useRef, useState } from "react";
import { API, apiFetch } from "@/utils/api";
import { groupByDay } from "@/utils/movementGroups";
import MovementCard from "./MovementCard";
import MovementSheet from "./MovementSheet";

const PAGE_SIZE = 30;
const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "classified", label: "Clasificados" },
  { key: "reconciled", label: "Conciliados" },
];

export default function MobileMovements() {
  const [filter, setFilter] = useState("all");
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [sheetTx, setSheetTx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadSeq = useRef(0);

  const loadCounts = useCallback(async () => {
    const entries = await Promise.all(
      FILTERS.map(async (f) => {
        const qs = f.key === "all" ? "" : `status=${f.key}&`;
        try {
          const res = await apiFetch(`${API}/bank/transactions?${qs}limit=1`);
          const d = res.ok ? await res.json() : { total: 0 };
          return [f.key, d.total ?? 0];
        } catch {
          return [f.key, 0];
        }
      })
    );
    setCounts(Object.fromEntries(entries));
  }, []);

  const loadSuggestions = useCallback(async (list) => {
    const ids = list
      .filter((t) => t.status === "pending" && t.category !== "nominas" && !t.reconciled_with && !t.classified_account)
      .map((t) => t.transaction_id)
      .slice(0, 50);
    if (ids.length === 0) return;
    try {
      const res = await apiFetch(`${API}/reconciliation/classify-suggestions`, {
        method: "POST",
        body: JSON.stringify({ transaction_ids: ids }),
      });
      if (res.ok) {
        const d = await res.json();
        setSuggestions((prev) => ({ ...prev, ...(d.suggestions || {}) }));
      }
    } catch (e) {
      console.error("Error cargando sugerencias", e);
    }
  }, []);

  const load = useCallback(
    async ({ append = false, status = filter, skip = 0 } = {}) => {
      const seq = ++loadSeq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      const params = new URLSearchParams();
      if (status !== "all") params.append("status", status);
      params.append("limit", PAGE_SIZE);
      params.append("skip", skip);
      try {
        const res = await apiFetch(`${API}/bank/transactions?${params}`);
        if (!res.ok) return;
        const d = await res.json();
        // Respuesta obsoleta: entre el envío y la respuesta se lanzó otra carga.
        if (seq !== loadSeq.current) return;
        const list = d.transactions || [];
        setTotal(d.total ?? 0);
        setTxs((prev) => (append ? [...prev, ...list] : list));
        loadSuggestions(list);
      } catch (e) {
        console.error("Error cargando movimientos", e);
      } finally {
        if (seq === loadSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [filter, loadSuggestions]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`${API}/reconciliation/classify-categories`);
        if (res.ok) setCategories((await res.json()).categories || []);
      } catch (e) {
        console.error("Error cargando categorías", e);
      }
    })();
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    load({ status: filter, skip: 0 });
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyClassified = useCallback(
    () => {
      // El servidor puede haber identificado un pago sin contabilizarlo.
      setSheetTx(null);
      loadCounts();
      load({ status: filter, skip: 0 });
    },
    [filter, load, loadCounts]
  );

  const groups = groupByDay(txs);

  return (
    <div data-testid="mobile-movements">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Movimientos</h1>
      <p className="mt-0.5 text-xs text-slate-500">
        {counts.all ?? 0} en total · {counts.pending ?? 0} por clasificar
      </p>

      {/* Filtros */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-2.5 bg-slate-50 flex gap-2 overflow-x-auto">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`mfilter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 px-3.5 py-2.5 min-h-[44px] rounded-full text-xs font-medium border ${
              filter === f.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200"
            }`}
          >
            {f.label} ({counts[f.key] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : (
        <>
          {groups.map((g) => (
            <div key={g.label}>
              <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{g.label}</p>
              <div className="flex flex-col gap-2">
                {g.items.map((tx) => (
                  <MovementCard
                    key={tx.transaction_id}
                    tx={tx}
                    suggestion={suggestions[tx.transaction_id]}
                    categories={categories}
                    onTap={setSheetTx}
                  />
                ))}
              </div>
            </div>
          ))}
          {txs.length === 0 && (
            <p className="mt-8 text-center text-sm text-slate-400">No hay movimientos con este filtro.</p>
          )}
          {txs.length < total && (
            <button
              data-testid="mload-more"
              onClick={() => load({ append: true, skip: txs.length })}
              disabled={loadingMore}
              className="mt-4 w-full min-h-[44px] rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50"
            >
              {loadingMore ? "Cargando…" : `Cargar más (${txs.length} de ${total})`}
            </button>
          )}
        </>
      )}

      <MovementSheet
        tx={sheetTx}
        suggestion={sheetTx ? suggestions[sheetTx.transaction_id] : null}
        categories={categories}
        onClose={() => setSheetTx(null)}
        onClassified={applyClassified}
      />
    </div>
  );
}
